import { useState } from 'react';
import { useHang, useHistory } from '@/lib/query';
import { fmt } from '@/lib/format';
import { Card, Chip, ProcessDiagram, SectionTitle, StatusPill, TrendChart, type ProcessNode } from '@/components/ui';
import { PageBanner } from '@/components/layout/PageBanner';

export default function HangMonitor() {
  const { data: units } = useHang();
  const [sel, setSel] = useState('hang-1');
  const u = units?.find((x) => x.id === sel) ?? units?.[0];
  const { data: hist } = useHistory(`do:${sel}`, 120);
  const farmHealth = units?.some((x) => x.health === 'alarm') ? 'alarm' : 'normal';

  const nodes: ProcessNode[] = u
    ? [
        { label: '进水', value: `${fmt(u.flowActual, 1)} m³/h`, sub: `目标 ${u.flowTarget}`, status: 'normal' },
        { label: 'LHO 增氧', value: u.lhoOn ? '开' : '关', sub: '强电并联', status: u.o2ValveOn ? 'normal' : 'warning' },
        { label: '养殖池', value: `DO ${fmt(u.do, 2)}`, sub: `${fmt(u.temp, 1)}℃ · pH ${fmt(u.ph, 2)}`, status: u.health },
        { label: '微滤 / 生化', value: `ORP ${u.orp}`, sub: `${fmt(u.freqHz, 1)} Hz 变频`, status: 'normal' },
        { label: '回水 / 排污', value: '周期', sub: 'PLC 同节拍', status: 'normal' },
      ]
    : [];

  return (
    <div>
      <PageBanner />
      <div className="mb-4">
        <h2 className="text-[24px] font-bold text-slate-900">吊养系统监控</h2>
        <p className="mt-1 text-[14px] text-slate-500">6 套吊养 + 7 号标准 RAS · 单系统工艺与远程参数（实时）</p>
        <div className="mt-1 text-[13px] text-slate-400">接口数据 · SSE 实时推送</div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <span className="pill border border-ok-border bg-ok-bg text-ok-fg">7号标准 RAS <StatusPill health="normal" className="ml-1" /></span>
        <span className="pill border border-alarm-border bg-alarm-bg text-alarm-fg">吊养（6套） <StatusPill health={farmHealth} className="ml-1" /></span>
      </div>

      <div className="mb-6 flex flex-wrap gap-2">
        {units?.map((unit) => (
          <Chip key={unit.id} active={unit.id === sel} onClick={() => setSel(unit.id)}>
            {unit.name}
          </Chip>
        ))}
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3 text-[13px] text-slate-500">
        <span>单系统工艺动态图（REQ-V6-002）→ {u?.name}</span>
        <div className="flex gap-1">
          {units?.map((unit, i) => (
            <button key={unit.id} onClick={() => setSel(unit.id)} className="h-7 w-7 rounded-md border border-hairline bg-white text-slate-600 hover:bg-slate-50">
              {i + 1}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="space-y-5 lg:col-span-2">
          <Card className="p-5">
            <SectionTitle title="单系统工艺动态图" desc="SVG 流程示意 · 节点绑定寄存器实时值" />
            <ProcessDiagram nodes={nodes} />
          </Card>
          <TrendChart title={`${u?.name ?? ''} · 溶解氧 DO 趋势`} samples={hist?.[0]?.samples ?? []} unit="mg/L" color="#17a8ab" />
        </div>

        <div className="space-y-4">
          <Card className="p-5">
            <div className="text-[15px] font-semibold text-slate-800">LHO 增氧</div>
            <div className="mt-2 text-[14px] text-slate-600">自吸泵 · 纯氧阀 · 强电并联（{u?.lhoOn ? '开' : '关'}）</div>
            <div className="mt-1 text-[13px] text-slate-400">DO 闭环：4.0–6.5 mg/L</div>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-[30px] font-bold tabular-nums text-slate-900">{fmt(u?.do, 2)}</span>
              <span className="text-[13px] text-slate-400">mg/L</span>
              {u ? <StatusPill health={u.health} className="ml-2" /> : null}
            </div>
          </Card>
          <Card className="p-5">
            <div className="text-[15px] font-semibold text-slate-800">变频恒流</div>
            <div className="mt-2 text-[14px] text-slate-600">目标 {u?.flowTarget} m³/h · 实采 {fmt(u?.flowActual, 1)}</div>
            <div className="mt-1 text-[13px] text-slate-400">{fmt(u?.freqHz, 1)} Hz · 自动调频 · 开</div>
          </Card>
          <Card className="p-5">
            <div className="text-[15px] font-semibold text-slate-800">自动排污 / 换水</div>
            <div className="mt-2 text-[14px] text-slate-600">{u?.blowdownCycle}</div>
          </Card>
        </div>
      </div>
    </div>
  );
}
